import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Flex, message } from 'antd';
import { Modal } from 'antd';
import { RefreshIcon } from '@/assets/icons/icons';
import theme from '@/assets/theme';
import { useToken } from '@/common/helper';
import { TriggerReportRecalculate, GetReportTaskStatus } from '@/apis/shipment';

export interface ReportRefreshButtonRef {
  triggerPolling: (taskId: string) => void;
}

interface ReportRecalculateBody {
  start_date?: string;
  end_date?: string;
  timezone_format?: string;
  [key: string]: unknown;
}

interface ReportRefreshButtonProps {
  reportModalOpen: boolean;
  lastRefreshTime: string | null;
  onRefreshComplete: () => void;
  getRecalculateBody: () => ReportRecalculateBody;
}

const formatLastRefreshTime = (time: string | null): string => {
  if (!time) {
    return 'Not available';
  }

  const now = new Date();
  const lastUpdate = new Date(time);
  const diffMs = now.getTime() - lastUpdate.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins === 1) return '1 min ago';
  if (diffMins < 60) return `${diffMins} mins ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
};

const getRemainingMinutes = (responseData: Record<string, unknown>): number => {
  const seconds = Number(responseData.estimated_seconds_remaining);
  if (Number.isFinite(seconds) && seconds > 0) {
    return Math.max(1, Math.ceil(seconds / 60));
  }

  return 10;
};

export const ReportRefreshButton = forwardRef<
  ReportRefreshButtonRef,
  ReportRefreshButtonProps
>(
  (
    { reportModalOpen, lastRefreshTime, onRefreshComplete, getRecalculateBody },
    ref
  ) => {
    const { checkTokenExpiry } = useToken();
    const [calculationStatus, setCalculationStatus] = useState<string>('');
    const [, setTaskId] = useState<string | null>(null);
    const pollingIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );
    const [formattedTimeString, setFormattedTimeString] = useState<string>('');
    const [remainingMinutes, setRemainingMinutes] = useState(10);

    const onRefreshCompleteRef = useRef(onRefreshComplete);
    useEffect(() => {
      onRefreshCompleteRef.current = onRefreshComplete;
    }, [onRefreshComplete]);

    const getRecalculateBodyRef = useRef(getRecalculateBody);
    useEffect(() => {
      getRecalculateBodyRef.current = getRecalculateBody;
    }, [getRecalculateBody]);

    useEffect(() => {
      setFormattedTimeString(formatLastRefreshTime(lastRefreshTime));

      const intervalId = setInterval(() => {
        setFormattedTimeString(formatLastRefreshTime(lastRefreshTime));
      }, 60000);

      return () => clearInterval(intervalId);
    }, [lastRefreshTime]);

    const stopPolling = useCallback(() => {
      if (pollingIntervalRef.current !== null) {
        clearTimeout(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }, []);

    const startPolling = useCallback(
      (id: string) => {
        stopPolling();
        setTaskId(id);
        setCalculationStatus('Calculating in background...');
        const currentToken = checkTokenExpiry() ?? '';

        const poll = async () => {
          try {
            const res = await GetReportTaskStatus(currentToken, id);
            const responseData = res?.data?.data?.data ?? {};
            const status: string = responseData.status ?? '';
            const upper = String(status).toUpperCase();

            setRemainingMinutes(getRemainingMinutes(responseData));

            if (upper === 'SUCCESS' || upper === 'COMPLETED') {
              stopPolling();
              setCalculationStatus('');
              setTaskId(null);
              message.success('Data refresh completed');
              Modal.confirm({
                title: 'New report available',
                content: 'Do you want to open and refresh the current data?',
                okText: 'Open new report',
                cancelText: 'Cancel',
                onOk: () => onRefreshCompleteRef.current(),
              });
            } else if (
              upper === 'FAILURE' ||
              upper === 'FAILED' ||
              upper === 'REVOKED'
            ) {
              stopPolling();
              setCalculationStatus('');
              setTaskId(null);
              message.error('Data recalculation failed. Please try again.');
            } else {
              pollingIntervalRef.current = setTimeout(poll, 5000);
            }
          } catch (err) {
            console.error('Polling error:', err);
            stopPolling();
            setCalculationStatus('');
            setTaskId(null);
          }
        };

        poll().catch((error: unknown) => {
          console.error('Polling error:', error);
        });
      },
      [checkTokenExpiry, stopPolling]
    );

    useImperativeHandle(ref, () => ({
      triggerPolling: (id: string) => {
        startPolling(id);
      },
    }));

    useEffect(() => {
      if (!reportModalOpen || theme.themeType === 2) return;

      let cancelled = false;
      const resumeActiveRefresh = async () => {
        try {
          const token = checkTokenExpiry() ?? '';
          const res = await GetReportTaskStatus(token);
          const responseData = res?.data?.data?.data ?? {};
          const status = String(responseData.status ?? '').toUpperCase();
          if (
            !cancelled &&
            (status === 'PENDING' || status === 'PROCESSING') &&
            responseData.task_id
          ) {
            startPolling(responseData.task_id);
          }
        } catch {
          // The report itself remains usable when background status is unavailable.
        }
      };

      resumeActiveRefresh().catch((): void => {
        // Restoring background refresh status is best-effort.
      });
      return () => {
        cancelled = true;
      };
    }, [checkTokenExpiry, reportModalOpen, startPolling]);

    useEffect(() => {
      if (!reportModalOpen) {
        stopPolling();
        setCalculationStatus('');
        setTaskId(null);
      }
    }, [reportModalOpen, stopPolling]);

    useEffect(() => {
      return () => {
        stopPolling();
      };
    }, [stopPolling]);

    const handleRecalculate = async () => {
      const token = checkTokenExpiry() ?? '';
      try {
        const body = getRecalculateBodyRef.current();
        const response = await TriggerReportRecalculate(token, body);
        // CustomResponse wraps endpoint payloads as { data: { data: payload } }.
        // Axios adds the outer response.data, so the task lives three levels deep.
        const result = response?.data?.data?.data;
        if (!result?.task_id) {
          throw new Error('Report recalculation did not return a task id');
        }
        startPolling(result.task_id);
      } catch (error) {
        console.error('Error triggering recalculation:', error);
        message.error('Failed to start recalculation');
      }
    };

    if (theme.themeType === 2) {
      return (
        <p className='zl-para-10 gray-5e italic download-report-last-refresh'>
          Last update: {formattedTimeString}
        </p>
      );
    }

    return (
      <Flex vertical gap={8} className='responsive-mt'>
        <Flex align='center'>
          <button
            onClick={handleRecalculate}
            disabled={!!calculationStatus}
            className={`zl-btn report-refresh-button radius-full${calculationStatus ? ' is-busy' : ''}`}
          >
            <span
              className={`loading-icon h-4 ${
                calculationStatus ? 'rotating' : ''
              }`}
            >
              <RefreshIcon color='var(--text-primary)' />
            </span>
            Refresh data
          </button>
          <p className='zl-para-10 gray-5e italic ml-2 download-report-last-refresh'>
            Last update: {formattedTimeString}
          </p>
        </Flex>
        {calculationStatus && (
          <div className='report-refresh-progress' role='status'>
            Data refreshing in the background, estimated time up to{' '}
            {remainingMinutes} {remainingMinutes === 1 ? 'min' : 'mins'}
          </div>
        )}
      </Flex>
    );
  }
);

ReportRefreshButton.displayName = 'ReportRefreshButton';
export default ReportRefreshButton;
